import { type Day } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useSupplierDeliverySettingDataQuery } from '../../../generated';

interface UseSupplierDeliverySettingsConfig {
  supplierId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSupplierDeliverySettings = ({
  supplierId,
  onSuccess,
  onError,
}: UseSupplierDeliverySettingsConfig) => {
  const { addNotification } = useNotification();

  const { data, loading, error, refetch } = useSupplierDeliverySettingDataQuery(
    {
      variables: { supplierId },
      skip: !supplierId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const mapDeliveryDays = useCallback(() => {
    if (!data?.supplierDeliverySetting) return null;

    const { cutoffTimes, cutoffType, deliveryDays, cutOffTime } =
      data.supplierDeliverySetting;

    // Map days to boolean values - true if NoCutoffSetting or WithCutoffSetting
    const deliveryDaysMap = {
      Monday: deliveryDays?.includes(1) || false,
      Tuesday: deliveryDays?.includes(2) || false,
      Wednesday: deliveryDays?.includes(3) || false,
      Thursday: deliveryDays?.includes(4) || false,
      Friday: deliveryDays?.includes(5) || false,
      Saturday: deliveryDays?.includes(6) || false,
      Sunday: deliveryDays?.includes(0) || false,
    } as Record<Day, boolean>;

    // Map cutoff times for each day - default to cutOffTime.time or 23:59:59
    const defaultTime = cutOffTime?.time || '23:59:59';
    const cutoffTimesMap = {
      Monday:
        cutoffTimes?.mon?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.mon.cutoffTime
          ? cutoffTimes.mon.cutoffTime
          : defaultTime,
      Tuesday:
        cutoffTimes?.tue?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.tue.cutoffTime
          ? cutoffTimes.tue.cutoffTime
          : defaultTime,
      Wednesday:
        cutoffTimes?.wed?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.wed.cutoffTime
          ? cutoffTimes.wed.cutoffTime
          : defaultTime,
      Thursday:
        cutoffTimes?.thu?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.thu.cutoffTime
          ? cutoffTimes.thu.cutoffTime
          : defaultTime,
      Friday:
        cutoffTimes?.fri?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.fri.cutoffTime
          ? cutoffTimes.fri.cutoffTime
          : defaultTime,
      Saturday:
        cutoffTimes?.sat?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sat.cutoffTime
          ? cutoffTimes.sat.cutoffTime
          : defaultTime,
      Sunday:
        cutoffTimes?.sun?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sun.cutoffTime
          ? cutoffTimes.sun.cutoffTime
          : defaultTime,
    } as Record<Day, string>;

    // Map cutoff days for each day - default to 0 or 1
    const defaultLeadTime = cutOffTime?.type === 'strict' ? '1' : '0';
    const cutoffDaysMap = {
      Monday:
        cutoffTimes?.mon?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.mon.leadTime !== undefined
          ? cutoffTimes.mon.leadTime.toString()
          : defaultLeadTime,
      Tuesday:
        cutoffTimes?.tue?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.tue.leadTime !== undefined
          ? cutoffTimes.tue.leadTime.toString()
          : defaultLeadTime,
      Wednesday:
        cutoffTimes?.wed?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.wed.leadTime !== undefined
          ? cutoffTimes.wed.leadTime.toString()
          : defaultLeadTime,
      Thursday:
        cutoffTimes?.thu?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.thu.leadTime !== undefined
          ? cutoffTimes.thu.leadTime.toString()
          : defaultLeadTime,
      Friday:
        cutoffTimes?.fri?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.fri.leadTime !== undefined
          ? cutoffTimes.fri.leadTime.toString()
          : defaultLeadTime,
      Saturday:
        cutoffTimes?.sat?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sat.leadTime !== undefined
          ? cutoffTimes.sat.leadTime.toString()
          : defaultLeadTime,
      Sunday:
        cutoffTimes?.sun?.__typename === 'WithCutoffSetting' &&
        cutoffTimes.sun.leadTime !== undefined
          ? cutoffTimes.sun.leadTime.toString()
          : defaultLeadTime,
    } as Record<Day, string>;

    return {
      deliveryDays: deliveryDaysMap,
      cutoffTimes: cutoffTimesMap,
      cutoffDays: cutoffDaysMap,
      cutoffType:
        cutoffType?.toLowerCase() === 'nocutoff'
          ? 'no_cutoff'
          : (cutoffType?.toLowerCase() ?? 'no_cutoff'),
      hasCustomDeliverySettings: true,
    };
  }, [data]);

  // Handle errors with notifications
  if (error) {
    addNotification({
      title: 'Error',
      message: 'Failed to load delivery settings',
      type: 'error',
    });
  }

  return {
    deliverySetting: data?.supplierDeliverySetting,
    mappedSettings: mapDeliveryDays(),
    isLoading: loading,
    error,
    refetch,
  };
};
